import { createElement, lazy, type ComponentType } from 'react'

/**
 * React.lazy plus a preload() that can make the FIRST render synchronous.
 *
 * Why plain React.lazy is not enough: even when its import() has already
 * resolved, lazy() suspends the first time it renders (it learns the result
 * through a .then callback, which runs a microtask later). On a cold page
 * load that commits the Suspense fallback, and React then holds the fallback
 * for its ~300ms throttle before revealing the page - measured 2026-10-01 as
 * a 300ms route spinner on every prerendered route, even with the chunk
 * cached.
 *
 * Here the module is captured as soon as it loads. Once it has, the returned
 * component renders the page directly and never touches the lazy wrapper, so
 * a caller that awaited preload() before the first render gets the page in
 * the first commit. Until then it is ordinary React.lazy, Suspense and all,
 * which is also exactly the behavior when a preload timed out or failed.
 *
 * A failed load is not cached: the next call (a later preload, or the lazy
 * wrapper's own first render) tries the import again, and a failure there
 * reaches the route error boundary as it always did.
 */
export type PreloadableComponent<P extends object> = ComponentType<P> & {
  preload: () => Promise<void>
}

export function lazyWithPreload<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>,
): PreloadableComponent<P> {
  let loaded: ComponentType<P> | null = null
  let pending: Promise<{ default: ComponentType<P> }> | null = null

  const load = () => {
    if (!pending) {
      pending = factory().then(
        (mod) => {
          loaded = mod.default
          return mod
        },
        (err: unknown) => {
          pending = null
          throw err
        },
      )
    }
    return pending
  }

  const Lazy = lazy(load)

  function Preloadable(props: P) {
    return loaded ? createElement(loaded, props) : createElement(Lazy, props)
  }
  Preloadable.preload = () => load().then(() => undefined)
  return Preloadable
}
